import { MapPin } from 'lucide-react';
import { StatusDot } from '@/components/shared/StatusDot';
import { TagBadge } from '@/components/shared/TagBadge';
import { TAG_COLORS } from '@/lib/constants';
import type { WeddingEvent } from '@/lib/types';

interface NowCardProps {
  event: WeddingEvent | null;
  progressPercent: number;
  isRunningOver: boolean;
  timeRemaining: string; // formatted countdown "MM:SS"
}

export function NowCard({ event, progressPercent, isRunningOver, timeRemaining }: NowCardProps) {
  if (!event) {
    return (
      <div
        className="mx-4 p-4 rounded-2xl"
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: 'rgba(148,163,184,0.3)',
            }}
          />
          <span
            className="text-[11px] font-semibold uppercase tracking-widest"
            style={{ color: 'var(--color-text-muted)' }}
          >
            NOW
          </span>
        </div>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '17px' }}>
          No active event right now
        </p>
      </div>
    );
  }

  const borderColor = isRunningOver
    ? 'var(--color-warning)'
    : TAG_COLORS[event.category].text;

  const barColor = isRunningOver ? 'var(--color-warning)' : 'var(--color-primary)';
  const clampedProgress = Math.min(100, Math.max(0, progressPercent));

  // Compute elapsed minutes label
  const now = Date.now();
  const startMs = new Date(event.startTime).getTime();
  const elapsedMins = Math.max(0, Math.round((now - startMs) / 60000));

  return (
    <div
      className="mx-4 p-4 rounded-2xl"
      style={{
        background: 'var(--color-surface)',
        borderLeft: `3px solid ${borderColor}`,
        borderTop: '1px solid var(--color-border)',
        borderRight: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
        borderRadius: '16px',
      }}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusDot status="active" size={10} />
          <span
            className="text-[11px] font-semibold uppercase tracking-widest"
            style={{ color: 'var(--color-text-muted)' }}
          >
            NOW
          </span>
        </div>
        <TagBadge category={event.category} />
      </div>

      {/* Event name */}
      <p
        className="font-semibold mb-1 leading-snug"
        style={{ fontSize: '22px', color: 'var(--color-text)' }}
      >
        {event.name}
      </p>

      {/* Location */}
      {event.location && (
        <div className="flex items-center gap-1 mb-3">
          <MapPin size={13} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            {event.location}
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div
        className="w-full rounded-full overflow-hidden mb-1"
        style={{ height: '4px', background: 'rgba(255,255,255,0.08)' }}
      >
        <div
          style={{
            height: '100%',
            width: `${clampedProgress}%`,
            background: barColor,
            borderRadius: '9999px',
            transition: 'width 1s linear',
          }}
        />
      </div>

      {/* Progress labels */}
      <div className="flex justify-between">
        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
          {elapsedMins} min in
        </span>
        {isRunningOver ? (
          <span style={{ fontSize: '11px', color: 'var(--color-warning)', fontWeight: 600 }}>
            Over by {Math.round((now - new Date(event.endTime).getTime()) / 60000)} min
          </span>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
            {timeRemaining} left
          </span>
        )}
      </div>
    </div>
  );
}
